import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  LogOut, TrendingDown, TrendingUp, Percent, ShoppingCart, Mail, Phone, Clock, Store, RefreshCw, Copy, CheckCircle2, Download, Loader2, Radio,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatMoney } from "../lib/apiClient";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { useAuth } from "../context/AuthContext";
import { EmailConsentPanel } from "../components/EmailConsentPanel";
import { AffiliatePanel } from "../components/AffiliatePanel";
import { WooMetricsPanel } from "../components/WooMetricsPanel";
import { ForecastPanel } from "../components/ForecastPanel";
import { CustomersPanel } from "../components/CustomersPanel";
import { ContactPanel } from "../components/ContactPanel";
import { TrackingPanel } from "../components/TrackingPanel";
import LiveVisitors from "../components/LiveVisitors";
import PushOnHold from "../components/PushOnHold";
import { FreeGiftToggle } from "../components/FreeGiftToggle";
import { FreeGiftReport } from "../components/FreeGiftReport";
import { BogoToggle } from "../components/BogoToggle";
import { SitewideSaleToggle } from "../components/SitewideSaleToggle";
import { CouponReport } from "../components/CouponReport";
import { TopProducts } from "../components/TopProducts";
import { LayoutDashboard, ShoppingCart as CartIcon, Mail as MailIcon, Handshake, LineChart as LineChartIcon, Users as UsersIcon, MessageSquare as MessageSquareIcon, Truck as TruckIcon } from "lucide-react";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "customers", label: "Customers", icon: UsersIcon },
  { id: "forecast", label: "Forecasting", icon: LineChartIcon },
  { id: "tracking", label: "Tracking", icon: TruckIcon },
  { id: "messages", label: "Messages", icon: MessageSquareIcon },
  { id: "carts", label: "Abandoned Carts", icon: CartIcon },
  { id: "emails", label: "Researcher Emails", icon: MailIcon },
  { id: "affiliates", label: "Affiliates", icon: Handshake },
];


const STATUS_STYLES = {
  "Recovered": "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  "Active cart": "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  "Pending USDC Tx": "bg-amber-500/15 text-amber-700 border-amber-500/30",
  "Wallet Connected": "bg-blue-500/15 text-blue-700 border-blue-500/30",
  "Abandoned at Payment": "bg-rose-500/15 text-rose-700 border-rose-500/30",
  "Abandoned at Cart": "bg-stone-500/15 text-stone-700 border-stone-500/30",
  "Draft": "bg-stone-500/15 text-stone-700 border-stone-500/30",
};

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3.6e6);
  if (h < 1) return `${Math.max(1, Math.floor(diff / 6e4))}m ago`;
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function Kpi({ icon: Icon, label, value, tone, testid }) {
  return (
    <div className="card-soft rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="eyebrow">{label}</span>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${tone}`}><Icon className="w-4 h-4" /></div>
      </div>
      <p className="font-display font-extrabold text-2xl sm:text-3xl mt-3">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [carts, setCarts] = useState([]);
  const [liveCarts, setLiveCarts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState("overview");
  const [downloadingCarts, setDownloadingCarts] = useState(false);

  const downloadCartsCsv = async () => {
    setDownloadingCarts(true);
    try {
      const response = await api.get("/admin/abandoned-carts/export", {
        params: filter !== "all" ? { status: filter } : {}, responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `ethos-abandoned-carts-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Couldn't export carts. Try again.");
    } finally {
      setDownloadingCarts(false);
    }
  };

  useEffect(() => { if (user === false) navigate("/admin"); }, [user, navigate]);

  const load = () => {
    setRefreshing(true);
    // allSettled so one failing endpoint (e.g. a newer call on an older deployed
    // backend) can't blank the whole dashboard — each section loads independently.
    Promise.allSettled([
      api.get("/admin/analytics"),
      api.get("/admin/abandoned-carts", { params: filter !== "all" ? { status: filter } : {} }),
      api.get("/admin/live-carts"),
    ]).then(([a, c, l]) => {
      if (a.status === "fulfilled") setAnalytics(a.value.data);
      if (c.status === "fulfilled") setCarts(c.value.data);
      if (l.status === "fulfilled") setLiveCarts(l.value.data);
    }).finally(() => setRefreshing(false));
  };

  const copyEmail = (email) => {
    navigator.clipboard?.writeText(email).then(() => toast.success("Email copied")).catch(() => {});
  };

  const recoveryMailto = (c) => {
    const lines = (c.items || []).map((i) => `• ${i.name} × ${i.quantity}`).join("\n");
    const subject = "Your Ethos Research Labs cart is waiting";
    const body = `Hi ${c.name || "there"},\n\nWe noticed you left a few items in your cart:\n${lines}\n\nCart total: ${formatMoney(c.cart_value)}\n\nYou can finish checking out here: ${window.location.origin}/shop\n\nIf you have any questions, just reply to this email.\n\n— Ethos Research Labs`;
    return `mailto:${c.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const markRecovered = async (sessionId) => {
    try {
      await api.post(`/admin/abandoned-carts/${sessionId}/recover`);
      toast.success("Marked as recovered");
      setSelected(null);
      load();
    } catch { toast.error("Couldn't update that cart."); }
  };

  useEffect(() => {
    if (user && user.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filter]);

  // Poll live carts every 20s while the Abandoned Carts tab is open.
  useEffect(() => {
    if (tab !== "carts" || !user || user.role !== "admin") return;
    const refreshLive = () => api.get("/admin/live-carts").then((r) => setLiveCarts(r.data)).catch(() => {});
    const id = setInterval(refreshLive, 20000);
    return () => clearInterval(id);
  }, [tab, user]);

  if (!user) return <div className="min-h-screen flex items-center justify-center text-muted-foreground page-cream">Loading…</div>;

  return (
    <div className="min-h-screen page-cream">
      <header className="bg-[#f4f1e9]/90 backdrop-blur-md border-b border-border sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/assets/ethos-research-labs-logo.webp" alt="Ethos Research Labs" width="990" height="509" className="h-12 w-auto object-contain mix-blend-multiply" data-testid="admin-header-logo" />
            <span className="hidden md:block text-xs font-bold tracking-widest text-muted-foreground">STORE ADMIN</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/")} className="hidden sm:flex items-center gap-2 pill px-4 h-9 bg-white border border-border text-sm font-semibold hover:bg-secondary" data-testid="view-store-button">
              <Store className="w-4 h-4" /> Storefront
            </button>
            <button onClick={load} className="w-9 h-9 rounded-full bg-white border border-border flex items-center justify-center hover:bg-secondary" data-testid="refresh-button">
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            </button>
            <button onClick={() => { logout(); navigate("/admin"); }} className="w-9 h-9 rounded-full bg-white border border-border flex items-center justify-center hover:bg-destructive/10 hover:text-destructive" data-testid="logout-button">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="font-display font-extrabold text-3xl">Store Admin</h1>
          <p className="text-muted-foreground text-sm mt-1">Revenue, carts, researcher emails and affiliate applications in one place</p>
        </div>

        <div className="flex gap-2 overflow-x-auto mb-8 border-b border-border" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[#c59b27] text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && <><LiveVisitors /><BogoToggle /><SitewideSaleToggle /><FreeGiftToggle /><TopProducts /><FreeGiftReport /><CouponReport /><PushOnHold /><WooMetricsPanel refreshing={refreshing} /></>}

        {tab === "customers" && <CustomersPanel refreshing={refreshing} />}

        {tab === "forecast" && <ForecastPanel refreshing={refreshing} />}
        {tab === "tracking" && <TrackingPanel refreshing={refreshing} />}
        {tab === "messages" && <ContactPanel refreshing={refreshing} />}

        {tab === "emails" && <EmailConsentPanel refreshing={refreshing} />}

        {tab === "affiliates" && <AffiliatePanel refreshing={refreshing} />}

        {tab === "carts" && (
        <div data-testid="carts-tab">
        <div className="card-soft rounded-2xl overflow-hidden mb-8" data-testid="live-carts-section">
          <div className="p-6 flex items-center justify-between gap-4 border-b border-border">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
              </span>
              <h3 className="font-display font-bold text-lg">Live Carts</h3>
              <span className="text-xs text-muted-foreground">active in the last 10 min</span>
            </div>
            <span className="pill px-3 py-1 text-xs font-semibold bg-emerald-500/15 text-emerald-700 border border-emerald-500/30" data-testid="live-carts-count">{liveCarts.length} live</span>
          </div>
          {liveCarts.length === 0 ? (
            <div className="px-6 py-12 text-center text-muted-foreground" data-testid="no-live-carts">
              <Radio className="w-6 h-6 mx-auto mb-2 opacity-50" />
              No one is shopping right now. Live carts appear here as shoppers add items.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {liveCarts.map((c) => (
                <div key={c.session_id} data-testid={`live-cart-${c.session_id}`} className="px-6 py-4 flex items-start justify-between gap-4 hover:bg-[#f9f6ef] transition-colors">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm">{c.name || c.email || "Anonymous shopper"}</p>
                      <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> {timeAgo(c.updated_at)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 break-words">
                      {(c.items || []).map((i) => `${i.name}${i.variant ? ` (${i.variant})` : ""} ×${i.quantity}`).join(" · ") || "Empty"}
                    </p>
                  </div>
                  <p className="font-display font-bold text-sm whitespace-nowrap">{formatMoney(c.cart_value)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <Kpi testid="kpi-abandoned-value" icon={TrendingDown} tone="bg-rose-500/15 text-rose-600" label="Abandoned Value" value={analytics ? formatMoney(analytics.total_abandoned_value) : "—"} />
          <Kpi testid="kpi-recovery-rate" icon={Percent} tone="bg-blue-500/15 text-blue-600" label="Recovery Rate" value={analytics ? `${analytics.recovery_rate}%` : "—"} />
          <Kpi testid="kpi-revenue-recovered" icon={TrendingUp} tone="bg-emerald-500/15 text-emerald-600" label="Revenue Recovered" value={analytics ? formatMoney(analytics.revenue_recovered) : "—"} />
          <Kpi testid="kpi-recovered-orders" icon={ShoppingCart} tone="bg-amber-500/15 text-amber-600" label="Recovered Orders" value={analytics ? analytics.recovered_orders : "—"} />
        </div>

        <div className="grid lg:grid-cols-2 gap-5 mb-8">
          <div className="card-soft rounded-2xl p-6">
            <h3 className="font-display font-bold mb-4">Abandoned vs Recovered (7d)</h3>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={analytics?.trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="date" stroke="#9c9488" fontSize={11} />
                <YAxis stroke="#9c9488" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2dcd0", borderRadius: 12 }} />
                <Line type="monotone" dataKey="abandoned" stroke="#e11d48" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="recovered" stroke="#059669" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
            <div className="flex gap-6 mt-3 text-xs">
              <span className="flex items-center gap-2"><span className="w-3 h-0.5 bg-rose-500" /> Abandoned</span>
              <span className="flex items-center gap-2"><span className="w-3 h-0.5 bg-emerald-600" /> Recovered</span>
            </div>
          </div>
          <div className="card-soft rounded-2xl p-6">
            <h3 className="font-display font-bold mb-4">Daily volume</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={analytics?.trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="date" stroke="#9c9488" fontSize={11} />
                <YAxis stroke="#9c9488" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2dcd0", borderRadius: 12 }} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                <Bar dataKey="abandoned" fill="#c0952f" radius={[6, 6, 0, 0]} />
                <Bar dataKey="recovered" fill="#059669" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-soft rounded-2xl overflow-hidden">
          <div className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border">
            <h3 className="font-display font-bold text-lg">Abandoned Carts</h3>
            <div className="flex items-center gap-2 flex-wrap">
              {["all", "new", "recovered"].map((s) => (
                <button key={s} onClick={() => setFilter(s)} data-testid={`cart-filter-${s}`} className={`pill px-3 py-1.5 text-xs font-semibold capitalize border transition-colors ${filter === s ? "btn-ink border-transparent" : "bg-white text-muted-foreground border-border hover:text-foreground"}`}>{s}</button>
              ))}
              <button onClick={downloadCartsCsv} disabled={downloadingCarts || carts.length === 0} data-testid="download-carts-csv-button"
                className="pill px-3 py-1.5 text-xs font-semibold btn-gold inline-flex items-center gap-1.5 disabled:opacity-50">
                {downloadingCarts ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                {downloadingCarts ? "Preparing…" : "Download CSV"}
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground text-xs uppercase tracking-wider border-b border-border bg-[#f6f1e6]">
                  <th className="px-6 py-3 font-bold">Shopper</th>
                  <th className="px-6 py-3 font-bold">Items</th>
                  <th className="px-6 py-3 font-bold">Cart Value</th>
                  <th className="px-6 py-3 font-bold">Status</th>
                  <th className="px-6 py-3 font-bold">Abandoned</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {carts.length === 0 ? (
                  <tr><td colSpan={6} className="px-6 py-12 text-center text-muted-foreground" data-testid="no-carts">No abandoned carts.</td></tr>
                ) : (
                  carts.map((c) => (
                    <tr key={c.session_id} data-testid={`abandoned-cart-row-${c.session_id}`} className="border-b border-border last:border-0 hover:bg-[#f9f6ef] transition-colors">
                      <td className="px-6 py-4"><p className="font-semibold">{c.name || "Anonymous"}</p><p className="text-xs text-muted-foreground">{c.email || "no email"}</p></td>
                      <td className="px-6 py-4 text-muted-foreground">{(c.items || []).reduce((s, i) => s + i.quantity, 0)} items</td>
                      <td className="px-6 py-4 font-display font-bold">{formatMoney(c.cart_value)}</td>
                      <td className="px-6 py-4"><span className={`inline-block px-2.5 py-1 pill text-[11px] font-semibold border ${STATUS_STYLES[c.payment_intent_status] || STATUS_STYLES.Draft}`}>{c.payment_intent_status}</span></td>
                      <td className="px-6 py-4 text-muted-foreground"><span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {timeAgo(c.created_at)}</span></td>
                      <td className="px-6 py-4 text-right"><button onClick={() => setSelected(c)} data-testid={`view-cart-${c.session_id}`} className="font-semibold text-xs hover:underline" style={{ color: "var(--gold)" }}>Details</button></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </div>
        )}
      </main>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md bg-[#f4f1e9] border-l border-border" data-testid="cart-detail-drawer">
          <SheetHeader><SheetTitle className="font-display font-bold">Cart details</SheetTitle><SheetDescription className="text-xs text-muted-foreground">Abandoned cart contents and recovery actions.</SheetDescription></SheetHeader>
          {selected && (
            <div className="mt-6 space-y-5">
              <div className="card-soft rounded-xl p-4">
                <p className="font-semibold">{selected.name || "Anonymous"}</p>
                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <p className="flex items-center gap-2"><Mail className="w-4 h-4" /> {selected.email || "—"}</p>
                  <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> {selected.phone || "—"}</p>
                </div>
              </div>
              <div>
                <p className="eyebrow mb-2">Cart contents</p>
                <div className="space-y-2">
                  {(selected.items || []).map((i, idx) => (
                    <div key={idx} className="flex gap-3 items-center card-soft rounded-xl p-3">
                      {i.image_url && <img src={i.image_url} alt={i.name} className="w-11 h-11 rounded-lg object-cover bg-secondary" />}
                      <div className="flex-1 min-w-0"><p className="text-sm truncate font-medium">{i.name}</p><p className="text-xs text-muted-foreground">Qty {i.quantity} · {formatMoney(i.price)}</p></div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card-soft rounded-xl p-4 flex justify-between items-center">
                <span className="text-muted-foreground text-sm">Cart value</span>
                <span className="font-display font-bold">{formatMoney(selected.cart_value)}</span>
              </div>
              {selected.status === "recovered" ? (
                <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3" data-testid="cart-recovered-note">
                  <CheckCircle2 className="w-4 h-4" /> Recovered{selected.recovered_order_number ? ` · ${selected.recovered_order_number}` : ""}
                </div>
              ) : (
                <div className="space-y-2">
                  {selected.email ? (
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => copyEmail(selected.email)} data-testid="copy-email-button" className="h-11 rounded-xl border border-border font-semibold text-sm flex items-center justify-center gap-2 hover:bg-secondary"><Copy className="w-4 h-4" /> Copy email</button>
                      <a href={recoveryMailto(selected)} data-testid="email-shopper-button"><button className="w-full h-11 rounded-xl border border-border font-semibold text-sm flex items-center justify-center gap-2 hover:bg-secondary"><Mail className="w-4 h-4" /> Email shopper</button></a>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground bg-secondary/50 rounded-lg p-3">No email captured for this cart — the shopper didn't reach the email step.</p>
                  )}
                  <button onClick={() => markRecovered(selected.session_id)} data-testid="mark-recovered-button" className="w-full h-11 rounded-xl btn-ink font-display font-bold flex items-center justify-center gap-2"><CheckCircle2 className="w-4 h-4" /> Mark recovered</button>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
